import React, { useState, useEffect, useCallback } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import { createPortal } from "react-dom";
import AdminLayout from "../../layouts/AdminLayout";

// Yacht background images for random selection
const yachtBackgrounds = [
    '/images/yacht-bg.jpg',
    '/images/yacht-bg-2.jpg', 
    '/images/yacht-bg-3.jpg',
    '/images/yacht-bg-4.jpg',
    '/images/yacht-bg-5.jpg',
    '/images/yacht-bg-6.jpg'
];

// Get random background image
const getRandomBackground = () => {
    const randomIndex = Math.floor(Math.random() * yachtBackgrounds.length);
    return yachtBackgrounds[randomIndex];
};

function ViewYachtDetails() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [yacht, setYacht] = useState(null);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [isEditing, setIsEditing] = useState(false);
    const [formData, setFormData] = useState({});
    const [saving, setSaving] = useState(false);
    const [uploadingImage, setUploadingImage] = useState(false);
    const [successMessage, setSuccessMessage] = useState("");
    const [images, setImages] = useState([]);
    const [showDeleteModal, setShowDeleteModal] = useState(false);
    const [deleting, setDeleting] = useState(false);
    const [showImageModal, setShowImageModal] = useState(false);
    const [selectedImageIndex, setSelectedImageIndex] = useState(0);
    const [backgroundImage] = useState(() => getRandomBackground());
    const [showImagePreviewModal, setShowImagePreviewModal] = useState(false);
    const [selectedFiles, setSelectedFiles] = useState([]);
    const [previewImageUrls, setPreviewImageUrls] = useState([]);
    const [imageDescriptions, setImageDescriptions] = useState({});
    const [currentUploadIndex, setCurrentUploadIndex] = useState(0);
    const [totalFilesToUpload, setTotalFilesToUpload] = useState(0);
    const [showDeleteImageModal, setShowDeleteImageModal] = useState(false);
    const [deletingImage, setDeletingImage] = useState(false);
    const [imageTransitioning, setImageTransitioning] = useState(false);

    // Yat tipini sayıdan metne çeviren fonksiyon
    const getYachtTypeText = (typeNumber) => {
        const types = {
            1: "Motor Yacht",
            2: "Sailing Yacht",
            3: "Catamaran",
            4: "Luxury Yacht",
            5: "Super Yacht",
        };
        return types[typeNumber] || "Bilinmiyor";
    };

    // Para birimi sembolü
    const getCurrencySymbol = (currency) => {
        const symbols = {
            TRY: "₺",
            EUR: "€",
            USD: "$",
            GBP: "£",
        };
        return symbols[currency] || currency;
    };

    // Modal işlemleri
    const openImageModal = (index) => {
        setSelectedImageIndex(index);
        setShowImageModal(true);
        document.body.style.overflow = 'hidden';
    };

    const closeImageModal = () => {
        setShowImageModal(false);
        setSelectedImageIndex(0);
        document.body.style.overflow = 'unset';
    };

    const nextImage = useCallback(() => {
        if (imageTransitioning) return;
        setImageTransitioning(true);
        setTimeout(() => {
            setSelectedImageIndex((prev) => (prev + 1) % images.length);
            setTimeout(() => setImageTransitioning(false), 50);
        }, 200);
    }, [images.length, imageTransitioning]);

    const prevImage = useCallback(() => {
        if (imageTransitioning) return;
        setImageTransitioning(true);
        setTimeout(() => {
            setSelectedImageIndex((prev) => (prev - 1 + images.length) % images.length);
            setTimeout(() => setImageTransitioning(false), 50);
        }, 200);
    }, [images.length, imageTransitioning]);

    // Klavye kontrolleri
    useEffect(() => {
        const handleKeyDown = (e) => {
            if (!showImageModal) return;
            
            if (e.key === 'Escape') {
                closeImageModal();
            } else if (e.key === 'ArrowRight') {
                nextImage();
            } else if (e.key === 'ArrowLeft') {
                prevImage();
            }
        };

        document.addEventListener('keydown', handleKeyDown);
        return () => document.removeEventListener('keydown', handleKeyDown);
    }, [showImageModal, images.length, nextImage, prevImage]);



    // Yat detaylarını API'den çek
    const fetchYachtDetails = async () => {
        try {
            setLoading(true);

            const token = localStorage.getItem("authToken");
            if (!token) {
                throw new Error("Giriş yapmanız gerekiyor");
            }

            const response = await fetch(`/rest/Yacht/getYachtById?id=${id}`, {
                method: "GET",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                if (response.status === 401) {
                    throw new Error("Token geçersiz veya süresi dolmuş");
                }
                if (response.status === 404) {
                    throw new Error("Yat bulunamadı");
                }
                throw new Error("Yat detayları getirilemedi");
            }

            const data = await response.json();
            setYacht(data);
            setFormData(data);
            setError(null);

            // Yat verisinde resim bilgileri varsa images state'ine ata
            if (data.yachtImages && Array.isArray(data.yachtImages)) {
                setImages(data.yachtImages);
            } else {
                setImages([]);
            }

            // Yat detayları yüklendikten sonra resimleri de çek (geçici olarak devre dışı)
            // await fetchYachtImages();
        } catch (error) {
            console.error("Yat detaylarını getirme hatası:", error);
            setError(
                error.message || "Yat detayları yüklenirken bir hata oluştu"
            );
        } finally {
            setLoading(false);
        }
    };

    // Form verilerini güncelle
    const handleInputChange = (e) => {
        const { name, value } = e.target;

        // Sayısal alanlar için tip dönüşümü yapmayalım, string olarak saklayalım
        // API çağrısında dönüştüreceğiz
        setFormData((prev) => ({
            ...prev,
            [name]: value,
        }));
    };

    // Yat bilgilerini güncelle
    const handleUpdateYacht = async () => {
        try {
            setSaving(true);
            setError("");
            setSuccessMessage("");

            const token = localStorage.getItem("authToken");
            if (!token) {
                throw new Error("Giriş yapmanız gerekiyor");
            }

            const updateData = {
                ...formData,
                id: parseInt(id),
                type: parseInt(formData.type) || yacht.type,
                capacity: parseInt(formData.capacity) || yacht.capacity,
                year: parseInt(formData.year) || yacht.year,
                length: parseFloat(formData.length) || yacht.length,
                grossTone: parseFloat(formData.grossTone) || yacht.grossTone,
                price: parseFloat(formData.price) || yacht.price,
            };

            const response = await fetch("/rest/Yacht/updateYacht", {
                method: "PUT",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(updateData),
            });

            if (!response.ok) {
                throw new Error("Yat güncellenirken hata oluştu");
            }

            const responseData = await response.json();
            console.log("Update response:", responseData);

            // Mevcut yacht verilerini güncellenmiş formData ile birleştir
            const updatedYachtData = {
                ...yacht,
                ...formData,
                // Sayısal değerleri doğru tiplerde tut
                id: parseInt(id),
                type: parseInt(formData.type) || yacht.type,
                capacity: parseInt(formData.capacity) || yacht.capacity,
                year: parseInt(formData.year) || yacht.year,
                length: parseFloat(formData.length) || yacht.length,
                grossTone: parseFloat(formData.grossTone) || yacht.grossTone,
                price: parseFloat(formData.price) || yacht.price,
            };

            console.log("Updated yacht data:", updatedYachtData);
            setYacht(updatedYachtData);
            setFormData(updatedYachtData);
            setIsEditing(false);
            setSuccessMessage("Yat bilgileri başarıyla güncellendi!");

            setTimeout(() => setSuccessMessage(""), 3000);
        } catch (error) {
            console.error("Yat güncelleme hatası:", error);
            setError(error.message || "Yat güncellenirken bir hata oluştu");
        } finally {
            setSaving(false);
        }
    };

    // Yat silme
    const handleDeleteYacht = async () => {
        try {
            setDeleting(true);
            setError("");

            const token = localStorage.getItem("authToken");
            if (!token) {
                throw new Error("Giriş yapmanız gerekiyor");
            }

            const response = await fetch(`/rest/Yacht/deleteYacht/${id}`, {
                method: "DELETE",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
            });

            if (!response.ok) {
                throw new Error("Yat silinirken hata oluştu");
            }

            setSuccessMessage("Yat başarıyla silindi!");
            setTimeout(() => {
                navigate("/admin/yachts");
            }, 1500);
        } catch (error) {
            console.error("Yat silme hatası:", error);
            setError(error.message || "Yat silinirken bir hata oluştu");
        } finally {
            setDeleting(false);
            setShowDeleteModal(false);
        }
    };

    // Resim önizleme modalını açma
    const handleImageSelect = (files) => {
        if (files && files.length > 0) {
            setSelectedFiles(files);
            setTotalFilesToUpload(files.length);
            setCurrentUploadIndex(0);
            
            // Preview URL'leri oluştur
            const previewPromises = files.map(file => {
                return new Promise((resolve) => {
                    const reader = new FileReader();
                    reader.onload = (e) => {
                        resolve({
                            file: file,
                            preview: e.target.result,
                            name: file.name,
                            size: file.size
                        });
                    };
                    reader.readAsDataURL(file);
                });
            });
            
            Promise.all(previewPromises).then(previews => {
                setPreviewImageUrls(previews);
                // Initialize descriptions object
                const initialDescriptions = {};
                previews.forEach((_, index) => {
                    initialDescriptions[index] = '';
                });
                setImageDescriptions(initialDescriptions);
                setShowImagePreviewModal(true);
            });
        }
    };

    // Tek bir resmi listeden çıkarma
    const removeImageFromList = (indexToRemove) => {
        const newSelectedFiles = selectedFiles.filter((_, index) => index !== indexToRemove);
        const newPreviewUrls = previewImageUrls.filter((_, index) => index !== indexToRemove);
        
        // Update descriptions by reindexing
        const newDescriptions = {};
        Object.keys(imageDescriptions).forEach(key => {
            const index = parseInt(key);
            if (index < indexToRemove) {
                newDescriptions[index] = imageDescriptions[key];
            } else if (index > indexToRemove) {
                newDescriptions[index - 1] = imageDescriptions[key];
            }
        });
        
        setSelectedFiles(newSelectedFiles);
        setPreviewImageUrls(newPreviewUrls);
        setImageDescriptions(newDescriptions);
        setTotalFilesToUpload(newSelectedFiles.length);
        
        // Eğer hiç dosya kalmadıysa modal'ı kapat
        if (newSelectedFiles.length === 0) {
            closeImagePreviewModal();
        }
    };

    // Resim önizleme modalını kapatma
    // Resim açıklamasını güncelleme
    const handleDescriptionChange = (index, description) => {
        setImageDescriptions(prev => ({
            ...prev,
            [index]: description
        }));
    };

    // Resim silme fonksiyonu
    const deleteImageFromYacht = async (imageUrl) => {
        try {
            setDeletingImage(true);
            setError("");

            const token = localStorage.getItem("authToken");
            if (!token) {
                throw new Error("Giriş yapmanız gerekiyor");
            }

            const requestData = {
                fileName: imageUrl
            };

            const response = await fetch("/rest/Yacht/deleteImageFromYacht", {
                method: "POST",
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${token}`,
                },
                body: JSON.stringify(requestData),
            });

            if (!response.ok) {
                const errorText = await response.text();
                throw new Error(`Resim silinirken hata oluştu: ${errorText}`);
            }

            setSuccessMessage("Resim başarıyla silindi!");
            
            // Modal'ı kapat
            setShowDeleteImageModal(false);
            closeImageModal();
            
            // Yat verilerini yeniden yükle
            await fetchYachtDetails();
            
            setTimeout(() => {
                setSuccessMessage("");
            }, 3000);
        } catch (error) {
            console.error("Resim silme hatası:", error);
            setError(error.message || "Resim silinirken bir hata oluştu");
        } finally {
            setDeletingImage(false);
        }
    };

    // Resim silme onay modalını göster
    const showDeleteImageConfirmation = () => {
        setShowDeleteImageModal(true);
    };

    // Resim silme onay modalını kapat
    const hideDeleteImageConfirmation = () => {
        setShowDeleteImageModal(false);
    };

    // Resim önizleme modalını kapatma
    const closeImagePreviewModal = () => {
        setShowImagePreviewModal(false);
        setSelectedFiles([]);
        setPreviewImageUrls([]);
        setImageDescriptions({});
        setCurrentUploadIndex(0);
        setTotalFilesToUpload(0);
        // File input'u temizle
        const fileInput = document.getElementById('imageUpload');
        if (fileInput) {
            fileInput.value = '';
        }
    };

    // Tek bir resmi yükleme fonksiyonu
    const uploadSingleImage = async (file, order, description = '') => {
        const token = localStorage.getItem("authToken");
        if (!token) {
            throw new Error("Giriş yapmanız gerekiyor");
        }

        return new Promise((resolve, reject) => {
            const reader = new FileReader();
            reader.onloadend = async () => {
                try {
                    const base64String = reader.result.split(",")[1];

                    const imageData = {
                        yachtId: parseInt(id),
                        imageAsBytes: base64String,
                        description: description || null,
                        order: order,
                    };

                    const response = await fetch("/rest/Yacht/addImageToYacht", {
                        method: "POST",
                        headers: {
                            "Content-Type": "application/json",
                            Authorization: `Bearer ${token}`,
                        },
                        body: JSON.stringify(imageData),
                    });

                    if (!response.ok) {
                        throw new Error(`${file.name} yüklenirken hata oluştu`);
                    }

                    resolve({ success: true, fileName: file.name });
                } catch (error) {
                    reject({ error: error.message, fileName: file.name });
                }
            };
            reader.readAsDataURL(file);
        });
    };

    // Çoklu resim yüklemeyi onaylama
    const confirmImageUpload = async () => {
        if (!selectedFiles || selectedFiles.length === 0) return;

        try {
            setUploadingImage(true);
            setError("");
            setShowImagePreviewModal(false);

            let successCount = 0;
            let errorCount = 0;
            const errors = [];

            // Dosyaları tek tek yükle
            for (let i = 0; i < selectedFiles.length; i++) {
                setCurrentUploadIndex(i + 1);
                
                try {
                    const order = images.length + i + 1;
                    const description = imageDescriptions[i] || '';
                    await uploadSingleImage(selectedFiles[i], order, description);
                    successCount++;
                } catch (error) {
                    errorCount++;
                    errors.push(error.fileName);
                }
            }

            // Sonuç mesajı
            if (successCount > 0 && errorCount === 0) {
                setSuccessMessage(`${successCount} resim başarıyla yüklendi!`);
            } else if (successCount > 0 && errorCount > 0) {
                setSuccessMessage(`${successCount} resim başarıyla yüklendi, ${errorCount} resim yüklenemedi.`);
                setError(`Yüklenemeyen resimler: ${errors.join(', ')}`);
            } else {
                setError(`Hiçbir resim yüklenemedi: ${errors.join(', ')}`);
            }

            setTimeout(() => {
                setSuccessMessage("");
                setError("");
            }, 5000);

            // Yat verilerini yeniden yükle
            await fetchYachtDetails();
            
            // Seçilen dosyaları temizle
            setSelectedFiles([]);
            setPreviewImageUrls([]);
            setImageDescriptions({});
            setCurrentUploadIndex(0);
            setTotalFilesToUpload(0);
            
            // File input'u temizle
            const fileInput = document.getElementById('imageUpload');
            if (fileInput) {
                fileInput.value = '';
            }
        } catch (error) {
            console.error("Resim yükleme hatası:", error);
            setError(error.message || "Resimler yüklenirken bir hata oluştu");
        } finally {
            setUploadingImage(false);
            setCurrentUploadIndex(0);
        }
    };



    useEffect(() => {
        if (id) {
            fetchYachtDetails();
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [id]);



    if (loading) {
        return (
            <div 
                className="min-h-screen flex items-center justify-center relative"
                style={{
                    backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.85), rgba(30, 41, 59, 0.85)), url(${getRandomBackground()})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundAttachment: 'fixed'
                }}
            >
                <div className="text-center bg-white/10 backdrop-blur-xl border border-white/30 rounded-3xl p-8 shadow-2xl shadow-black/40">
                    <div className="animate-spin rounded-full h-20 w-20 border-4 border-white/30 border-t-white mx-auto mb-6"></div>
                    <p className="text-white/90 text-lg font-semibold">Yat detayları yükleniyor...</p>
                </div>
            </div>
        );
    }

    if (error) {
        return (
            <div 
                className="min-h-screen flex items-center justify-center relative"
                style={{
                    backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.85), rgba(30, 41, 59, 0.85)), url(${getRandomBackground()})`,
                    backgroundSize: 'cover',
                    backgroundPosition: 'center',
                    backgroundAttachment: 'fixed'
                }}
            >
                <div className="text-center">
                    <div className="bg-white/10 backdrop-blur-xl border border-white/30 rounded-3xl p-8 mb-6 max-w-md shadow-2xl shadow-black/40">
                        <div className="mb-4">
                            <div className="w-16 h-16 bg-red-500/20 backdrop-blur-sm border border-red-400/30 rounded-2xl flex items-center justify-center mx-auto mb-4">
                                <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.728-.833-2.498 0L4.316 16.5c-.77.833.192 2.5 1.732 2.5z" />
                                </svg>
                            </div>
                        </div>
                        <p className="text-white mb-6 font-semibold text-lg">{error}</p>
                        <Link
                            to="/admin/yachts"
                            className="px-6 py-3 bg-white/20 backdrop-blur-sm border border-white/30 hover:bg-white/30 text-white rounded-2xl transition-all duration-200 font-semibold shadow-lg shadow-black/20"
                        >
                            Yat Listesine Dön
                        </Link>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div
            className="min-h-screen text-gray-900 relative"
            style={{
                backgroundImage: `linear-gradient(rgba(15, 23, 42, 0.85), rgba(30, 41, 59, 0.85)), url(${backgroundImage})`,
                backgroundSize: 'cover',
                backgroundPosition: 'center',
                backgroundAttachment: 'fixed'
            }}
        >
            <AdminLayout currentPage="yachts" customWrapper={true} customContentClass="ml-64 p-8">
                    {/* Header */}
                    <div className="bg-white/10 backdrop-blur-xl border border-white/30 rounded-3xl p-8 mb-8 shadow-2xl shadow-black/40">
                        <div className="flex justify-between items-center">
                            <div className="flex items-center gap-6">
                                <Link
                                    to="/admin/yachts"
                                    className="p-3 bg-white/10 backdrop-blur-sm border border-white/30 rounded-2xl text-white hover:text-white/90 hover:bg-white/20 transition-all duration-200 group"
                                >
                                    <svg
                                        className="w-5 h-5 group-hover:-translate-x-1 transition-transform"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M15 19l-7-7 7-7"
                                        />
                                    </svg>
                                </Link>
                                <div>
                                    <h1 className="text-4xl font-bold text-white">
                                        {yacht?.name || "Yat Detayları"}
                                    </h1>
                                    <p className="text-white/80 text-base mt-2 font-medium">
                                        {getYachtTypeText(yacht?.type)} •{" "}
                                        {yacht?.model} • {yacht?.year}
                                    </p>
                                </div>
                            </div>
                            <div className="flex items-center gap-3">
                                {isEditing ? (
                                    <>
                                        <button
                                            onClick={handleUpdateYacht}
                                            disabled={saving}
                                            className="px-8 py-3 bg-white/20 backdrop-blur-sm border border-white/30 hover:bg-white/30 text-white rounded-2xl transition-all duration-200 flex items-center gap-2 disabled:opacity-50 shadow-lg shadow-black/20 font-semibold"
                                        >
                                            {saving ? (
                                                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                                            ) : (
                                                <svg
                                                    className="w-4 h-4"
                                                    fill="none"
                                                    stroke="currentColor"
                                                    viewBox="0 0 24 24"
                                                >
                                                    <path
                                                        strokeLinecap="round"
                                                        strokeLinejoin="round"
                                                        strokeWidth={2}
                                                        d="M5 13l4 4L19 7"
                                                    />
                                                </svg>
                                            )}
                                            {saving
                                                ? "Kaydediliyor..."
                                                : "Kaydet"}
                                        </button>
                                        <button
                                            onClick={() => {
                                                setIsEditing(false);
                                                setFormData(yacht);
                                                setError("");
                                            }}
                                            className="px-6 py-3 bg-white/10 backdrop-blur-sm border border-white/30 hover:bg-white/20 text-white/90 rounded-2xl transition-all duration-200 flex items-center gap-2 font-semibold"
                                        >
                                            <svg
                                                className="w-4 h-4"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M6 18L18 6M6 6l12 12"
                                                />
                                            </svg>
                                            İptal
                                        </button>
                                    </>
                                ) : (
                                    <>
                                        <button
                                            onClick={() => setIsEditing(true)}
                                            className="px-8 py-3 bg-white/20 backdrop-blur-sm border border-white/30 hover:bg-white/30 text-white rounded-2xl transition-all duration-200 flex items-center gap-2 shadow-lg shadow-black/20 font-semibold"
                                        >
                                            <svg
                                                className="w-4 h-4"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M15.232 5.232l3.536 3.536m-2.036-5.036a2.5 2.5 0 113.536 3.536L6.5 21.036H3v-3.572L16.732 3.732z"
                                                />
                                            </svg>
                                            Düzenle
                                        </button>
                                        <button
                                            onClick={() =>
                                                setShowDeleteModal(true)
                                            }
                                            className="px-6 py-3 bg-red-500/20 backdrop-blur-sm border border-red-400/30 hover:bg-red-500/30 text-red-200 rounded-2xl transition-all duration-200 flex items-center gap-2 font-semibold"
                                        >
                                            <svg
                                                className="w-4 h-4"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                                />
                                            </svg>
                                            Sil
                                        </button>
                                    </>
                                )}
                            </div>
                        </div>
                    </div>

                    {/* Bildirim Mesajları */}
                    {successMessage && (
                        <div className="mb-6 p-5 bg-green-500/20 backdrop-blur-xl border border-green-400/30 rounded-2xl shadow-xl shadow-black/20">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-white/20 backdrop-blur-sm rounded-full border border-white/30">
                                    <svg
                                        className="w-5 h-5 text-white"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M9 12l2 2 4-4m6 2a9 9 0 11-18 0 9 9 0 0118 0z"
                                        />
                                    </svg>
                                </div>
                                <span className="text-white font-semibold">
                                    {successMessage}
                                </span>
                            </div>
                        </div>
                    )}

                    {error && (
                        <div className="mb-6 p-5 bg-red-500/20 backdrop-blur-xl border border-red-400/30 rounded-2xl shadow-xl shadow-black/20">
                            <div className="flex items-center gap-3">
                                <div className="p-2 bg-white/20 backdrop-blur-sm rounded-full border border-white/30">
                                    <svg
                                        className="w-5 h-5 text-white"
                                        fill="none"
                                        stroke="currentColor"
                                        viewBox="0 0 24 24"
                                    >
                                        <path
                                            strokeLinecap="round"
                                            strokeLinejoin="round"
                                            strokeWidth={2}
                                            d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.728-.833-2.498 0L4.316 16.5c-.77.833.192 2.5 1.732 2.5z"
                                        />
                                    </svg>
                                </div>
                                <span className="text-white font-semibold">{error}</span>
                            </div>
                        </div>
                    )}

                    {yacht && (
                        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
                            {/* Ana Bilgiler */}
                            <div className="space-y-6">
                                {/* Temel Bilgiler */}
                                <div className="bg-white/10 backdrop-blur-xl border border-white/30 rounded-3xl p-8 shadow-2xl shadow-black/40">
                                    <h2 className="text-2xl font-bold mb-8 flex items-center gap-3 text-white">
                                        <div className="p-2 bg-white/20 backdrop-blur-sm rounded-xl border border-white/30">
                                            <svg
                                                className="w-6 h-6 text-white"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
                                                />
                                            </svg>
                                        </div>
                                        Temel Bilgiler
                                    </h2>
                                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                                        <div>
                                            <label className="block text-white/60 text-sm mb-2">
                                                Yat Adı
                                            </label>
                                            {isEditing ? (
                                                <input
                                                    type="text"
                                                    name="name"
                                                    value={formData.name || ""}
                                                    onChange={handleInputChange}
                                                    className="w-full px-4 py-3 bg-white/5 border border-white/10 rounded-xl text-white placeholder-white/30 focus:outline-none focus:border-blue-500 transition-all"
                                                />
                                            ) : (
                                                <p className="text-white font-semibold text-lg">
                                                    {yacht.name}
                                                </p>
                                            )}
                                        </div>
                                        <div>
                                            <label className="block text-white/90 text-sm font-semibold mb-3">
                                                Tip
                                            </label>
                                            {isEditing ? (
                                                <select
                                                    name="type"
                                                    value={formData.type || ""}
                                                    onChange={handleInputChange}
                                                    className="w-full px-4 py-4 bg-white/10 backdrop-blur-sm border border-white/30 rounded-2xl text-white focus:outline-none focus:border-white focus:ring-4 focus:ring-white/20 transition-all font-medium"
                                                >
                                                    <option value="">Seçiniz</option>
                                                    <option value={1}>Motor Yacht</option>
                                                    <option value={2}>Sailing Yacht</option>
                                                    <option value={3}>Catamaran</option>
                                                    <option value={4}>Luxury Yacht</option>
                                                    <option value={5}>Super Yacht</option>
                                                </select>
                                            ) : (
                                                <span className="inline-block px-6 py-3 bg-gradient-to-r from-blue-500 to-blue-600 text-white rounded-2xl text-sm font-bold shadow-lg shadow-blue-500/25">
                                                    {getYachtTypeText(yacht.type)}
                                                </span>
                                            )}
                                        </div>
                                        <div>
                                            <label className="block text-white/90 text-sm font-semibold mb-3">
                                                Model & Yıl
                                            </label>
                                            {isEditing ? (
                                                <div className="grid grid-cols-2 gap-3">
                                                <input
                                                    type="text"
                                                    name="model"
                                                    value={formData.model || ""}
                                                    onChange={handleInputChange}
                                                        placeholder="Model"
                                                        className="px-4 py-4 bg-white/10 backdrop-blur-sm border border-white/30 rounded-2xl text-white placeholder-white/50 focus:outline-none focus:border-white focus:ring-4 focus:ring-white/20 transition-all font-medium"
                                                    />
                                                    <input
                                                        type="number"
                                                        name="year"
                                                        value={formData.year || ""}
                                                        onChange={handleInputChange}
                                                        placeholder="Yıl"
                                                        className="px-4 py-4 bg-white/10 backdrop-blur-sm border border-white/30 rounded-2xl text-white placeholder-white/50 focus:outline-none focus:border-white focus:ring-4 focus:ring-white/20 transition-all font-medium"
                                                    />
                                                </div>
                                            ) : (
                                                <p className="text-white font-bold text-lg">
                                                    {yacht.model} ({yacht.year})
                                                </p>
                                            )}
                                        </div>
                                        <div>
                                            <label className="block text-white/90 text-sm font-semibold mb-3">
                                                Kaptan
                                            </label>
                                            {isEditing ? (
                                                <input
                                                    type="text"
                                                    name="captain"
                                                    value={formData.captain || ""}
                                                    onChange={handleInputChange}
                                                    className="w-full px-4 py-4 bg-white/10 backdrop-blur-sm border border-white/30 rounded-2xl text-white placeholder-white/50 focus:outline-none focus:border-white focus:ring-4 focus:ring-white/20 transition-all font-medium"
                                                />
                                            ) : (
                                                <p className="text-white font-bold text-lg">
                                                    {yacht.captain}
                                                </p>
                                            )}
                                        </div>
                                        <div>
                                            <label className="block text-white/90 text-sm font-semibold mb-3">
                                                Bayrak & Konum
                                            </label>
                                            {isEditing ? (
                                                <div className="grid grid-cols-2 gap-3">
                                                <input
                                                    type="text"
                                                    name="flag"
                                                    value={formData.flag || ""}
                                                    onChange={handleInputChange}
                                                        placeholder="Bayrak"
                                                        className="px-4 py-4 bg-white/10 backdrop-blur-sm border border-white/30 rounded-2xl text-white placeholder-white/50 focus:outline-none focus:border-white focus:ring-4 focus:ring-white/20 transition-all font-medium"
                                                    />
                                                    <input
                                                        type="text"
                                                        name="location"
                                                        value={formData.location || ""}
                                                        onChange={handleInputChange}
                                                        placeholder="Konum"
                                                        className="px-4 py-4 bg-white/10 backdrop-blur-sm border border-white/30 rounded-2xl text-white placeholder-white/50 focus:outline-none focus:border-white focus:ring-4 focus:ring-white/20 transition-all font-medium"
                                                    />
                                                </div>
                                            ) : (
                                                <p className="text-white font-bold text-lg">
                                                    {yacht.flag}{yacht.location && yacht.location !== "Belirtilmemiş" ? ` • ${yacht.location}` : ""}
                                                </p>
                                            )}
                                        </div>
                                        <div>
                                            <label className="block text-white/90 text-sm font-semibold mb-3">
                                                Mürettebat & Telefon
                                            </label>
                                            {isEditing ? (
                                                <div className="grid grid-cols-1 gap-3">
                                                    <input
                                                        type="text"
                                                        name="shipBoy"
                                                        value={formData.shipBoy || ""}
                                                        onChange={handleInputChange}
                                                        placeholder="Mürettebat"
                                                        className="px-4 py-4 bg-white/10 backdrop-blur-sm border border-white/30 rounded-2xl text-white placeholder-white/50 focus:outline-none focus:border-white focus:ring-4 focus:ring-white/20 transition-all font-medium"
                                                    />
                                                    <input
                                                        type="tel"
                                                        name="phone"
                                                        value={formData.phone || ""}
                                                        onChange={handleInputChange}
                                                        placeholder="Telefon"
                                                        className="px-4 py-4 bg-white/10 backdrop-blur-sm border border-white/30 rounded-2xl text-white placeholder-white/50 focus:outline-none focus:border-white focus:ring-4 focus:ring-white/20 transition-all font-medium"
                                                    />
                                                </div>
                                            ) : (
                                                <div className="text-white font-semibold space-y-2">
                                                    {yacht.shipBoy && yacht.shipBoy !== "Belirtilmemiş" && (
                                                        <p className="text-lg">{yacht.shipBoy}</p>
                                                    )}
                                                    {yacht.phone && (
                                                        <p className="text-white text-base">{yacht.phone}</p>
                                                    )}
                                                    {(!yacht.shipBoy || yacht.shipBoy === "Belirtilmemiş") && !yacht.phone && (
                                                        <p className="text-white/70 text-sm">Bilgi bulunmuyor</p>
                                                    )}
                                                </div>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                {/* Yat Resimleri */}
                                {images.length > 0 && (
                                    <div className="bg-white/10 backdrop-blur-xl border border-white/30 rounded-3xl p-8 shadow-2xl shadow-black/40">
                                        <h2 className="text-2xl font-bold mb-6 flex items-center gap-3 text-white">
                                            <div className="p-2 bg-white/20 backdrop-blur-sm rounded-xl border border-white/30">
                                                <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                </svg>
                                            </div>
                                            Yat Resimleri ({images.length})
                                        </h2>
                                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
                                            {images.map((image, index) => {
                                                // Resim URL'ini oluştur - yeni format
                                                const imageUrl = image.imageUrl ? `https://cyprusyachtclub.com/rest-images/YachtImages/${image.imageUrl}` : null;
                                                
                                                return (
                                                    <div 
                                                        key={image.id || index} 
                                                        className="relative group bg-white/10 backdrop-blur-md rounded-2xl overflow-hidden hover:scale-105 hover:shadow-xl transition-all duration-300 border border-white/30 cursor-pointer shadow-lg"
                                                        onClick={() => openImageModal(index)}
                                                    >
                                                        <div className="aspect-square bg-white/20 backdrop-blur-sm flex items-center justify-center">
                                                            {imageUrl ? (
                                                                <img
                                                                    src={imageUrl}
                                                                    alt={`Yat resmi ${index + 1}`}
                                                                    className="w-full h-full object-cover"
                                                                    onError={(e) => {
                                                                        console.log('Resim yüklenemedi:', imageUrl);
                                                                        e.target.style.display = 'none';
                                                                        e.target.nextElementSibling.style.display = 'flex';
                                                                    }}
                                                                />
                                                            ) : null}
                                                            <div className={`${imageUrl ? 'hidden' : 'flex'} aspect-square w-full bg-white/20 backdrop-blur-sm items-center justify-center text-white/70`}>
                                                                <svg className="w-8 h-8" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                                </svg>
                                                            </div>
                                                        </div>
                                                        <div className="absolute inset-0 bg-blue-900/80 opacity-0 group-hover:opacity-100 transition-opacity duration-200 flex items-center justify-center rounded-2xl">
                                                            <div className="text-center text-white">
                                                                <p className="text-sm font-semibold">Resim {image.order || index + 1}</p>
                                                                <p className="text-xs text-white/90 mt-1 font-medium">Büyütmek için tıklayın</p>
                                                            </div>
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}

                                {/* Teknik Özellikler */}
                                <div className="bg-white/10 backdrop-blur-xl border border-white/30 rounded-3xl p-8 shadow-2xl shadow-black/40">
                                    <h2 className="text-2xl font-bold mb-8 flex items-center gap-3 text-white">
                                        <div className="p-2 bg-white/20 backdrop-blur-sm rounded-xl border border-white/30">
                                            <svg
                                                className="w-6 h-6 text-white"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M9.663 17h4.673M12 3v1m6.364 1.636l-.707.707M21 12h-1M4 12H3m3.343-5.657l-.707-.707m2.828 9.9a5 5 0 117.072 0l-.548.547A3.374 3.374 0 0014 18.469V19a2 2 0 11-4 0v-.531c0-.895-.356-1.754-.988-2.386l-.548-.547z"
                                                />
                                            </svg>
                                        </div>
                                        Teknik Özellikler
                                    </h2>
                                    <div className="grid grid-cols-3 gap-6">
                                        <div className="text-center p-6 bg-white/15 backdrop-blur-md border border-white/30 rounded-2xl shadow-lg">
                                            {isEditing ? (
                                                <>
                                                    <input
                                                        type="number"
                                                        name="length"
                                                        value={formData.length || ""}
                                                        onChange={handleInputChange}
                                                        className="w-full text-center text-2xl font-bold bg-transparent border-b-2 border-blue-300 text-white focus:outline-none focus:border-blue-600 mb-2 pb-1"
                                                        step="0.1"
                                                    />
                                                    <div className="text-white text-sm font-semibold">Uzunluk (m)</div>
                                                </>
                                            ) : (
                                                <>
                                                    <div className="text-3xl font-bold text-white mb-2">{yacht.length}</div>
                                                    <div className="text-white text-sm font-bold">METRE</div>
                                                    <div className="text-white/70 text-xs font-medium">Uzunluk</div>
                                                </>
                                            )}
                                        </div>
                                        <div className="text-center p-6 bg-white/15 backdrop-blur-md border border-white/30 rounded-2xl shadow-lg">
                                            {isEditing ? (
                                                <>
                                                    <input
                                                        type="number"
                                                        name="capacity"
                                                        value={formData.capacity || ""}
                                                        onChange={handleInputChange}
                                                        className="w-full text-center text-2xl font-bold bg-transparent border-b-2 border-indigo-300 text-indigo-900 focus:outline-none focus:border-indigo-600 mb-2 pb-1"
                                                    />
                                                    <div className="text-indigo-600 text-sm font-semibold">Kapasite</div>
                                                </>
                                            ) : (
                                                <>
                                                    <div className="text-3xl font-bold text-white mb-2">{yacht.capacity}</div>
                                                    <div className="text-white/90 text-sm font-bold">KİŞİ</div>
                                                    <div className="text-white/70 text-xs font-medium">Kapasite</div>
                                                </>
                                            )}
                                        </div>
                                        <div className="text-center p-6 bg-white/15 backdrop-blur-md border border-white/30 rounded-2xl shadow-lg">
                                            {isEditing ? (
                                                <>
                                                    <input
                                                        type="number"
                                                        name="grossTone"
                                                        value={formData.grossTone || ""}
                                                        onChange={handleInputChange}
                                                        className="w-full text-center text-2xl font-bold bg-transparent border-b-2 border-cyan-300 text-cyan-900 focus:outline-none focus:border-cyan-600 mb-2 pb-1"
                                                        step="0.1"
                                                    />
                                                    <div className="text-cyan-600 text-sm font-semibold">Gross Tone</div>
                                                </>
                                            ) : (
                                                <>
                                                    <div className="text-3xl font-bold text-white mb-2">{yacht.grossTone || "0"}</div>
                                                    <div className="text-white/90 text-sm font-bold">TON</div>
                                                    <div className="text-white/70 text-xs font-medium">Gross Tone</div>
                                                </>
                                            )}
                                        </div>
                                    </div>
                                    {(yacht.engineInformation && yacht.engineInformation !== "Belirtilmemiş") || isEditing ? (
                                        <div className="mt-6">
                                        <label className="block text-white/90 text-sm font-semibold mb-3">
                                            Motor Bilgileri
                                        </label>
                                        {isEditing ? (
                                            <textarea
                                                name="engineInformation"
                                                    value={formData.engineInformation || ""}
                                                onChange={handleInputChange}
                                                    rows="3"
                                                className="w-full px-4 py-4 bg-white/10 backdrop-blur-sm border border-white/30 rounded-2xl text-white placeholder-white/50 focus:outline-none focus:border-white focus:ring-4 focus:ring-white/20 transition-all resize-none font-medium"
                                                placeholder="Motor bilgilerini giriniz..."
                                            />
                                        ) : (
                                                <div className="bg-white/15 backdrop-blur-sm rounded-2xl p-4 border border-white/30">
                                                    <p className="text-white text-base font-medium">
                                                        {yacht.engineInformation}
                                                </p>
                                            </div>
                                        )}
                                    </div>
                                    ) : null}
                                </div>

                                {/* Açıklama ve Özellikler */}
                                {((yacht.description && yacht.description !== "Belirtilmemiş") || 
                                  (yacht.features && yacht.features !== "Belirtilmemiş") || 
                                  isEditing) && (
                                <div className="bg-white/10 backdrop-blur-xl border border-white/30 rounded-3xl p-8 shadow-2xl shadow-black/40">
                                    <h2 className="text-2xl font-bold mb-8 flex items-center gap-3 text-white">
                                        <div className="p-2 bg-white/20 backdrop-blur-sm rounded-xl border border-white/30">
                                            <svg
                                                className="w-6 h-6 text-white"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M4 6h16M4 12h16m-7 6h7"
                                                />
                                            </svg>
                                        </div>
                                        Açıklama ve Özellikler
                                    </h2>
                                    <div className="space-y-6">
                                        {((yacht.description && yacht.description !== "Belirtilmemiş") || isEditing) && (
                                        <div>
                                            <label className="block text-white/90 text-sm font-semibold mb-3">Açıklama</label>
                                    {isEditing ? (
                                        <textarea
                                            name="description"
                                            value={formData.description || ""}
                                            onChange={handleInputChange}
                                                    rows="4"
                                            className="w-full px-4 py-4 bg-white/10 backdrop-blur-sm border border-white/30 rounded-2xl text-white placeholder-white/50 focus:outline-none focus:border-white focus:ring-4 focus:ring-white/20 transition-all resize-none font-medium"
                                            placeholder="Yat açıklamasını giriniz..."
                                        />
                                    ) : (
                                        <div className="bg-white/15 backdrop-blur-sm rounded-2xl p-5 border border-white/30">
                                                    <p className="text-white leading-relaxed text-base font-medium">
                                                        {yacht.description}
                                            </p>
                                        </div>
                                    )}
                                        </div>
                                        )}
                                        {((yacht.features && yacht.features !== "Belirtilmemiş") || isEditing) && (
                                        <div>
                                            <label className="block text-white/90 text-sm font-semibold mb-3">Özellikler</label>
                                    {isEditing ? (
                                        <textarea
                                            name="features"
                                            value={formData.features || ""}
                                            onChange={handleInputChange}
                                                    rows="4"
                                            className="w-full px-4 py-4 bg-white/10 backdrop-blur-sm border border-white/30 rounded-2xl text-white placeholder-white/50 focus:outline-none focus:border-white focus:ring-4 focus:ring-white/20 transition-all resize-none font-medium"
                                            placeholder="Yat özelliklerini giriniz..."
                                        />
                                    ) : (
                                        <div className="bg-white/15 backdrop-blur-sm rounded-2xl p-5 border border-white/30">
                                                    <p className="text-white leading-relaxed text-base font-medium">
                                                        {yacht.features}
                                            </p>
                                        </div>
                                    )}
                                        </div>
                                        )}
                                    </div>
                                </div>
                                )}
                            </div>

                            {/* Sağ Sidebar - Fiyat ve Özet */}
                            <div className="space-y-8">
                                {/* Fiyat Bilgisi */}
                                <div className="bg-gradient-to-br from-blue-600 to-blue-700 border border-blue-500 rounded-3xl p-8 shadow-xl">
                                    <h3 className="text-xl font-bold text-white mb-6 flex items-center gap-3">
                                        <div className="p-2 bg-white/20 rounded-xl">
                                            <svg className="w-6 h-6 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8c-1.657 0-3 .895-3 2s1.343 2 3 2 3 .895 3 2-1.343 2-3 2m0-8c1.11 0 2.08.402 2.599 1M12 8V7m0 1v8m0 0v1m0-1c-1.11 0-2.08-.402-2.599-1" />
                                            </svg>
                                        </div>
                                        Fiyat
                                        </h3>
                                    {isEditing ? (
                                        <div className="space-y-4">
                                            <div>
                                                <input
                                                    type="number"
                                                    name="price"
                                                    value={formData.price || ""}
                                                    onChange={handleInputChange}
                                                    className="w-full px-4 py-4 bg-white/20 border border-white/30 rounded-2xl text-white placeholder-white/60 focus:outline-none focus:border-white focus:ring-4 focus:ring-white/20 transition-all font-semibold"
                                                    placeholder="Fiyat"
                                                    step="0.01"
                                                />
                                            </div>
                                            <div>
                                                <select
                                                    name="currency"
                                                    value={formData.currency || ""}
                                                    onChange={handleInputChange}
                                                    className="w-full px-4 py-4 bg-white/20 border border-white/30 rounded-2xl text-white focus:outline-none focus:border-white focus:ring-4 focus:ring-white/20 transition-all font-semibold"
                                                >
                                                    <option value="" className="text-gray-900">Para Birimi</option>
                                                    <option value="TRY" className="text-gray-900">₺ Türk Lirası</option>
                                                    <option value="EUR" className="text-gray-900">€ Euro</option>
                                                    <option value="USD" className="text-gray-900">$ Dolar</option>
                                                    <option value="GBP" className="text-gray-900">£ Sterlin</option>
                                                </select>
                                            </div>
                                        </div>
                                    ) : (
                                        <div className="text-center">
                                            <div className="text-4xl font-bold text-white mb-3">
                                                {yacht.price} {getCurrencySymbol(yacht.currency)}
                                            </div>
                                            <div className="text-blue-100 text-base px-4 py-2 bg-white/20 rounded-2xl inline-block font-medium">
                                                Günlük Kiralama
                                            </div>
                                        </div>
                                    )}
                                </div>

                                {/* Özet Bilgiler */}
                                <div className="bg-white/10 backdrop-blur-xl border border-white/30 rounded-3xl p-8 shadow-2xl shadow-black/40">
                                    <h3 className="text-xl font-bold mb-6 flex items-center gap-3 text-white">
                                        <div className="p-2 bg-white/20 backdrop-blur-sm rounded-xl border border-white/30">
                                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5H7a2 2 0 00-2 2v6a2 2 0 002 2h6a2 2 0 002-2V7a2 2 0 00-2-2h-2M9 5a2 2 0 002 2h2a2 2 0 002-2M9 5a2 2 0 012-2h2a2 2 0 012 2" />
                                            </svg>
                                        </div>
                                        Özet
                                    </h3>
                                    <div className="space-y-6">
                                        <div className="grid grid-cols-2 gap-6 text-base">
                                        <div>
                                                <div className="text-white mb-2 font-semibold text-sm">Uzunluk</div>
                                                <div className="text-white font-bold">{yacht.length}m</div>
                                        </div>
                                        <div>
                                                <div className="text-white mb-2 font-semibold text-sm">Kapasite</div>
                                                <div className="text-white font-bold">{yacht.capacity} kişi</div>
                                        </div>
                                        <div>
                                                <div className="text-white mb-2 font-semibold text-sm">Kaptan</div>
                                                <div className="text-white font-bold">{yacht.captain}</div>
                                                        </div>
                                                        <div>
                                                <div className="text-white mb-2 font-semibold text-sm">Bayrak</div>
                                                <div className="text-white font-bold">{yacht.flag}</div>
                                                        </div>
                                                    </div>
                                        {(yacht.phone || (yacht.location && yacht.location !== "Belirtilmemiş")) && (
                                        <div className="pt-4 border-t border-blue-200">
                                            <div className="text-white text-sm font-semibold mb-3">İletişim</div>
                                            {yacht.phone && (
                                                <div className="text-white font-bold text-base">{yacht.phone}</div>
                                            )}
                                            {yacht.location && yacht.location !== "Belirtilmemiş" && (
                                                <div className="text-white/90 text-sm mt-2 font-medium">{yacht.location}</div>
                                            )}
                                        </div>
                                        )}
                                        {yacht.yachtImages && yacht.yachtImages.length > 0 && (
                                            <div className="pt-4 border-t border-blue-200">
                                                <div className="text-white text-sm font-semibold mb-3">Resimler</div>
                                                <div className="text-white font-bold text-base flex items-center gap-2">
                                                    <div className="p-1 bg-green-100 rounded-full">
                                                        <svg className="w-4 h-4 text-green-600" fill="currentColor" viewBox="0 0 20 20">
                                                            <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                                    </svg>
                                                    </div>
                                                    {yacht.yachtImages.length} adet resim mevcut
                                                            </div>
                                            </div>
                                        )}
                                    </div>
                                </div>

                                {/* Resim Yönetimi */}
                                <div className="bg-white/10 backdrop-blur-xl border border-white/30 rounded-3xl p-8 shadow-2xl shadow-black/40">
                                    <h3 className="text-xl font-bold mb-6 flex items-center gap-3 text-white">
                                        <div className="p-2 bg-white/20 backdrop-blur-sm rounded-xl border border-white/30">
                                            <svg className="w-5 h-5 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                            </svg>
                                        </div>
                                        Resim Yönetimi
                                    </h3>
                                    
                                    {/* Resim Yükleme */}
                                    <div className="space-y-6">
                                        <div className="border-2 border-dashed border-white/40 rounded-2xl p-8 text-center hover:border-white/60 hover:bg-white/10 transition-all duration-300 group">
                                            <input
                                                type="file"
                                                accept="image/*"
                                                multiple
                                                onChange={(e) => {
                                                    const files = Array.from(e.target.files);
                                                    if (files.length > 0) {
                                                        handleImageSelect(files);
                                                    }
                                                }}
                                                className="hidden"
                                                id="imageUpload"
                                            />
                                            <label htmlFor="imageUpload" className="cursor-pointer block">
                                                {uploadingImage ? (
                                                    <div className="flex flex-col items-center gap-4">
                                                        <div className="animate-spin rounded-full h-10 w-10 border-3 border-blue-600 border-t-transparent"></div>
                                                        <span className="text-white/90 text-base font-semibold">Yükleniyor...</span>
                                                    </div>
                                                ) : (
                                                    <div className="flex flex-col items-center gap-4">
                                                        <div className="p-4 bg-white/20 backdrop-blur-sm rounded-2xl group-hover:bg-white/30 border border-white/30 transition-colors">
                                                            <svg className="w-8 h-8 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12" />
                                                            </svg>
                                                        </div>
                                                        <div>
                                                            <span className="text-white text-base font-bold block">Resim(ler) Ekle</span>
                                                            <span className="text-white text-sm mt-1 block font-medium">JPG, PNG • Çoklu seçim yapabilirsiniz</span>
                                                        </div>
                                                    </div>
                                                )}
                                            </label>
                                        </div>

                                        {/* Mevcut Resimler Listesi */}
                                        {images.length > 0 && (
                                            <div className="space-y-4">
                                                <div className="text-white/90 text-base font-bold">
                                                    Mevcut Resimler ({images.length})
                                                </div>
                                                <div className="max-h-60 overflow-y-auto space-y-3">
                                                    {images.map((image, index) => (
                                                        <div key={image.id || index} className="flex items-center gap-4 p-4 bg-white/10 backdrop-blur-sm rounded-2xl border border-white/30 hover:bg-white/20 transition-colors">
                                                            <div className="w-14 h-14 bg-white/20 backdrop-blur-sm rounded-xl flex items-center justify-center border border-blue-300 flex-shrink-0">
                                                                <svg className="w-7 h-7 text-white" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                                </svg>
                                                            </div>
                                                            <div className="flex-1 min-w-0">
                                                                <p className="text-white text-base font-bold truncate">
                                                                    Resim {image.order || (index + 1)}
                                                                </p>
                                                                <p className="text-white text-sm font-medium">
                                                                    {image.imageUrl ? 'Aktif' : 'Yüklendi'}
                                                                </p>
                                                            </div>
                                                            <div className="flex items-center">
                                                                <span className="text-green-600 text-sm font-semibold flex items-center gap-2 bg-green-100 px-3 py-1 rounded-full">
                                                                    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 20 20">
                                                                        <path fillRule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clipRule="evenodd" />
                                                                    </svg>
                                                                    Aktif
                                                                </span>
                                                            </div>
                                                        </div>
                                                    ))}
                                                </div>
                                            </div>
                                        )}

                                        {images.length === 0 && (
                                            <div className="text-center py-8">
                                                <p className="text-white text-base font-semibold">Henüz resim eklenmemiş</p>
                                                <p className="text-white/70 text-sm mt-2">Yukarıdaki alandan resim ekleyebilirsiniz</p>
                                            </div>
                                        )}
                                    </div>
                                </div>

                            </div>
                        </div>
                    )}

            {/* Image Preview Modal */}
            {showImagePreviewModal && (
                <div className="fixed inset-0 bg-black/60 backdrop-blur-xl flex items-center justify-center z-50">
                    <div className="bg-white/10 backdrop-blur-xl border border-white/30 rounded-3xl p-8 max-w-4xl w-full mx-4 shadow-2xl shadow-black/40 max-h-[90vh] overflow-y-auto">
                        <div className="text-center">
                            <h3 className="text-2xl font-bold text-white mb-6">
                                {previewImageUrls.length === 1 
                                    ? "Resmi Eklemek İstiyor Musunuz?" 
                                    : `${previewImageUrls.length} Resmi Eklemek İstiyor Musunuz?`}
                            </h3>
                            
                            {/* Resim Önizlemeleri */}
                            {previewImageUrls.length > 0 && (
                                <div className="mb-8">
                                    {previewImageUrls.length === 1 ? (
                                        /* Tek resim için büyük önizleme */
                                        <div className="mb-4 relative group inline-block text-center w-full">
                                            <div className="relative inline-block">
                                                {/* Çarpı (X) Butonu */}
                                                <button
                                                    onClick={() => removeImageFromList(0)}
                                                    className="absolute -top-3 -right-3 w-8 h-8 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-lg font-bold z-10 opacity-0 group-hover:opacity-100 transition-all duration-200 shadow-lg border-2 border-white"
                                                    title="Resmi listeden çıkar"
                                                >
                                                    ×
                                                </button>
                                                <img
                                                    src={previewImageUrls[0].preview}
                                                    alt="Resim Önizlemesi"
                                                    className="max-w-full max-h-80 object-contain rounded-2xl mx-auto shadow-lg border border-white/20"
                                                />
                                            </div>
                                            <p className="text-white/80 text-sm mt-2 font-medium">
                                                {previewImageUrls[0].name} ({(previewImageUrls[0].size / (1024 * 1024)).toFixed(2)} MB)
                                            </p>
                                            {/* Tek resim için açıklama alanı */}
                                            <div className="mt-4 w-full max-w-md mx-auto">
                                                <label className="block text-white/90 text-sm font-semibold mb-2">
                                                    Resim Açıklaması
                                                </label>
                                                <textarea
                                                    value={imageDescriptions[0] || ''}
                                                    onChange={(e) => handleDescriptionChange(0, e.target.value)}
                                                    placeholder="Bu resim için bir açıklama giriniz... (opsiyonel)"
                                                    rows="3"
                                                    className="w-full px-4 py-3 bg-white/10 backdrop-blur-sm border border-white/30 rounded-2xl text-white placeholder-white/50 focus:outline-none focus:border-white focus:ring-4 focus:ring-white/20 transition-all resize-none font-medium text-sm"
                                                />
                                            </div>
                                        </div>
                                    ) : (
                                        /* Çoklu resim için grid önizleme */
                                        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mb-4">
                                            {previewImageUrls.map((preview, index) => (
                                                <div key={index} className="bg-white/10 rounded-2xl p-3 border border-white/20 relative group">
                                                    {/* Çarpı (X) Butonu */}
                                                    <button
                                                        onClick={() => removeImageFromList(index)}
                                                        className="absolute -top-2 -right-2 w-6 h-6 bg-red-500 hover:bg-red-600 text-white rounded-full flex items-center justify-center text-xs font-bold z-10 opacity-0 group-hover:opacity-100 transition-all duration-200 shadow-lg border-2 border-white"
                                                        title="Resmi listeden çıkar"
                                                    >
                                                        ×
                                                    </button>
                                                    <img
                                                        src={preview.preview}
                                                        alt={`Önizleme ${index + 1}`}
                                                        className="w-full h-24 object-cover rounded-xl mb-2"
                                                    />
                                                    <p className="text-white text-xs font-medium truncate" title={preview.name}>
                                                        {preview.name}
                                                    </p>
                                                    <p className="text-white/70 text-xs mb-2">
                                                        {(preview.size / (1024 * 1024)).toFixed(1)} MB
                                                    </p>
                                                    {/* Çoklu resim için açıklama alanı */}
                                                    <div className="mt-2">
                                                        <textarea
                                                            value={imageDescriptions[index] || ''}
                                                            onChange={(e) => handleDescriptionChange(index, e.target.value)}
                                                            placeholder="Açıklama..."
                                                            rows="2"
                                                            className="w-full px-2 py-1 bg-white/10 backdrop-blur-sm border border-white/30 rounded-lg text-white placeholder-white/50 focus:outline-none focus:border-white focus:ring-2 focus:ring-white/20 transition-all resize-none font-medium text-xs"
                                                        />
                                                    </div>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            )}
                            
                            <p className="text-white/80 mb-8 text-base">
                                {previewImageUrls.length === 1 
                                    ? "Bu resim yat galerisine eklenecektir." 
                                    : `Bu ${previewImageUrls.length} resim yat galerisine eklenecektir.`} 
                                Devam etmek istediğinizden emin misiniz?
                            </p>
                            
                            {/* Yükleme Progress Bar */}
                            {uploadingImage && (
                                <div className="mb-6">
                                    <div className="bg-white/20 rounded-2xl p-6 border border-white/30">
                                        {/* Loading Spinner ve Başlık */}
                                        <div className="flex items-center justify-center mb-4">
                                            <div className="animate-spin rounded-full h-8 w-8 border-3 border-white/30 border-t-white mr-3"></div>
                                            <span className="text-white text-lg font-bold">
                                                {totalFilesToUpload > 1 
                                                    ? "Resimler Yükleniyor..." 
                                                    : "Resim Yükleniyor..."}
                                            </span>
                                        </div>
                                        
                                        {/* Progress Bar (sadece çoklu dosya için) */}
                                        {totalFilesToUpload > 1 && (
                                            <>
                                                <div className="flex justify-between items-center mb-3">
                                                    <span className="text-white text-sm font-medium">
                                                        İşlenen: {currentUploadIndex} / {totalFilesToUpload}
                                                    </span>
                                                    <span className="text-white text-sm font-medium">
                                                        %{Math.round((currentUploadIndex / totalFilesToUpload) * 100)}
                                                    </span>
                                                </div>
                                                <div className="w-full bg-white/10 rounded-full h-3">
                                                    <div 
                                                        className="bg-blue-500 h-3 rounded-full transition-all duration-300" 
                                                        style={{ width: `${(currentUploadIndex / totalFilesToUpload) * 100}%` }}
                                                    ></div>
                                                </div>
                                            </>
                                        )}
                                        
                                        {/* Lütfen bekleyin mesajı */}
                                        <p className="text-white/80 text-sm text-center mt-4 font-medium">
                                            Lütfen işlem tamamlanana kadar bekleyin...
                                        </p>
                                    </div>
                                </div>
                            )}
                            
                            <div className="flex gap-4 justify-center">
                                <button
                                    onClick={closeImagePreviewModal}
                                    className="px-8 py-3 bg-white/10 backdrop-blur-sm border border-white/30 hover:bg-white/20 text-white/90 rounded-2xl transition-all duration-200 font-semibold"
                                    disabled={uploadingImage}
                                >
                                    İptal
                                </button>
                                <button
                                    onClick={confirmImageUpload}
                                    disabled={uploadingImage}
                                    className="px-8 py-3 bg-blue-500/30 backdrop-blur-sm border border-blue-400/40 hover:bg-blue-500/40 text-white rounded-2xl transition-all duration-200 flex items-center gap-2 disabled:opacity-50 shadow-lg shadow-black/20 font-semibold"
                                >
                                    {uploadingImage ? (
                                        <>
                                            <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                                            {totalFilesToUpload > 1 ? `Yükleniyor... (${currentUploadIndex}/${totalFilesToUpload})` : "Yükleniyor..."}
                                        </>
                                    ) : (
                                        <>
                                            <svg
                                                className="w-5 h-5"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M5 13l4 4L19 7"
                                                />
                                            </svg>
                                            {previewImageUrls.length === 1 ? "Evet, Ekle" : `Evet, ${previewImageUrls.length} Resmi Ekle`}
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Image Modal */}
            {showImageModal && images.length > 0 && createPortal(
                <div className="fixed inset-0 bg-slate-900/70 backdrop-blur-xl flex items-center justify-center z-[9999]">
                    <div className="relative max-w-7xl max-h-full w-full h-full flex items-center justify-center p-6">
                        {/* Close Button */}
                        <button
                            onClick={closeImageModal}
                            className="absolute top-6 right-6 z-10 p-4 bg-white/20 hover:bg-white/30 text-white rounded-2xl transition-all duration-200 shadow-lg"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
                            </svg>
                        </button>

                        {/* Delete Button */}
                        <button
                            onClick={showDeleteImageConfirmation}
                            className="absolute top-6 right-24 z-10 p-3 bg-red-500/20 hover:bg-red-500/30 border border-red-400/30 text-red-200 rounded-xl transition-all duration-200 shadow-lg"
                            title="Resmi sil"
                        >
                            <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16" />
                            </svg>
                        </button>

                        {/* Previous Button */}
                        {images.length > 1 && (
                            <button
                                onClick={prevImage}
                                className="absolute left-6 top-1/2 transform -translate-y-1/2 z-10 p-4 bg-white/20 hover:bg-white/30 text-white rounded-2xl transition-all duration-200 shadow-lg"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 19l-7-7 7-7" />
                                </svg>
                            </button>
                        )}

                        {/* Next Button */}
                        {images.length > 1 && (
                            <button
                                onClick={nextImage}
                                className="absolute right-6 top-1/2 transform -translate-y-1/2 z-10 p-4 bg-white/20 hover:bg-white/30 text-white rounded-2xl transition-all duration-200 shadow-lg"
                            >
                                <svg className="w-6 h-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9 5l7 7-7 7" />
                                </svg>
                            </button>
                        )}

                        {/* Image Container */}
                        <div className="flex flex-col items-center justify-center max-w-full max-h-full">
                            {(() => {
                                const currentImage = images[selectedImageIndex];
                                const imageUrl = currentImage?.imageUrl 
                                    ? `https://cyprusyachtclub.com/rest-images/YachtImages/${currentImage.imageUrl}` 
                                    : null;
                                
                                return (
                                    <div className="relative">
                                        {imageUrl ? (
                                            <img
                                                src={imageUrl}
                                                alt={`Yat resmi ${selectedImageIndex + 1}`}
                                                className={`max-w-full max-h-[75vh] object-contain rounded-3xl shadow-2xl shadow-blue-900/50 transition-opacity duration-300 ${imageTransitioning ? 'opacity-0' : 'opacity-100'}`}
                                                onError={(e) => {
                                                    console.log('Modal resim yüklenemedi:', imageUrl);
                                                    e.target.style.display = 'none';
                                                    e.target.nextElementSibling.style.display = 'flex';
                                                }}
                                            />
                                        ) : null}
                                        <div className={`${imageUrl ? 'hidden' : 'flex'} w-96 h-96 bg-white/20 backdrop-blur-sm items-center justify-center text-white/90 rounded-3xl transition-opacity duration-300 ${imageTransitioning ? 'opacity-0' : 'opacity-100'}`}>
                                            <div className="text-center">
                                                <svg className="w-20 h-20 mx-auto mb-6" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M4 16l4.586-4.586a2 2 0 012.828 0L16 16m-2-2l1.586-1.586a2 2 0 012.828 0L20 14m-6-6h.01M6 20h12a2 2 0 002-2V6a2 2 0 00-2-2H6a2 2 0 00-2 2v12a2 2 0 002 2z" />
                                                </svg>
                                                <p className="text-xl font-bold">Resim yüklenemedi</p>
                                            </div>
                                        </div>
                                    </div>
                                );
                            })()}

                            {/* Image Info */}
                            <div className="mt-6 text-center">
                                <p className="text-white text-xl font-bold">
                                    {images[selectedImageIndex]?.description || `Resim ${images[selectedImageIndex]?.order || (selectedImageIndex + 1)}`}
                                </p>
                                {images.length > 1 && (
                                    <p className="text-white/90 text-base mt-2 font-medium">
                                        {selectedImageIndex + 1} / {images.length}
                                    </p>
                                )}
                                <p className="text-white/80 text-sm mt-3 font-medium">
                                    ESC: Kapat • ←→: Gezinme • Sil: Resmi sil
                                </p>
                            </div>
                        </div>

                        {/* Click outside to close */}
                        <div
                            className="absolute inset-0 -z-10"
                            onClick={closeImageModal}
                        ></div>
                    </div>
                </div>,
                document.body
            )}

            {/* Delete Image Confirmation Modal */}
            {showDeleteImageModal && (
                <div className="fixed inset-0 bg-slate-900/90 backdrop-blur-xl flex items-center justify-center z-50">
                    <div className="bg-white/10 backdrop-blur-xl border border-white/30 rounded-3xl p-8 max-w-md w-full mx-4 shadow-2xl shadow-black/40">
                        <div className="text-center">
                            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-2xl bg-red-500/20 backdrop-blur-sm border border-red-400/30 mb-6">
                                <svg
                                    className="h-8 w-8 text-white"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                    />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-bold text-white mb-3">
                                Resmi Sil
                            </h3>
                            <p className="text-white/80 mb-2 text-base font-semibold">
                                {images[selectedImageIndex]?.description || `Resim ${selectedImageIndex + 1}`}
                            </p>
                            <p className="text-white/60 mb-8 text-sm">
                                Bu resmi silmek istediğinizden emin misiniz? Bu işlem geri alınamaz.
                            </p>
                            <div className="flex gap-4 justify-center">
                                <button
                                    onClick={hideDeleteImageConfirmation}
                                    className="px-8 py-3 bg-white/10 backdrop-blur-sm border border-white/30 hover:bg-white/20 text-white/90 rounded-2xl transition-all duration-200 font-semibold"
                                    disabled={deletingImage}
                                >
                                    İptal
                                </button>
                                <button
                                    onClick={() => deleteImageFromYacht(images[selectedImageIndex]?.imageUrl)}
                                    disabled={deletingImage}
                                    className="px-8 py-3 bg-red-500/30 backdrop-blur-sm border border-red-400/40 hover:bg-red-500/40 text-white rounded-2xl transition-all duration-200 flex items-center gap-2 disabled:opacity-50 shadow-lg shadow-black/20 font-semibold"
                                >
                                    {deletingImage ? (
                                        <>
                                            <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                                            Siliniyor...
                                        </>
                                    ) : (
                                        <>
                                            <svg
                                                className="w-5 h-5"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                                />
                                            </svg>
                                            Evet, Sil
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Delete Confirmation Modal */}
            {showDeleteModal && (
                <div className="fixed inset-0 bg-slate-900/90 backdrop-blur-xl flex items-center justify-center z-50">
                    <div className="bg-white/10 backdrop-blur-xl border border-white/30 rounded-3xl p-8 max-w-md w-full mx-4 shadow-2xl shadow-black/40">
                        <div className="text-center">
                            <div className="mx-auto flex items-center justify-center h-16 w-16 rounded-2xl bg-red-500/20 backdrop-blur-sm border border-red-400/30 mb-6">
                                <svg
                                    className="h-8 w-8 text-white"
                                    fill="none"
                                    viewBox="0 0 24 24"
                                    stroke="currentColor"
                                >
                                    <path
                                        strokeLinecap="round"
                                        strokeLinejoin="round"
                                        strokeWidth={2}
                                        d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-2.5L13.732 4c-.77-.833-1.728-.833-2.498 0L4.316 16.5c-.77.833.192 2.5 1.732 2.5z"
                                    />
                                </svg>
                            </div>
                            <h3 className="text-2xl font-bold text-white mb-3">
                                Yat Silinecektir
                            </h3>
                            <p className="text-white/80 mb-8 text-base">
                                Bu işlem geri alınamaz. Yatı silmek
                                istediğinizden emin misiniz?
                            </p>
                            <div className="flex gap-4 justify-center">
                                <button
                                    onClick={() => setShowDeleteModal(false)}
                                    className="px-8 py-3 bg-white/10 backdrop-blur-sm border border-white/30 hover:bg-white/20 text-white/90 rounded-2xl transition-all duration-200 font-semibold"
                                    disabled={deleting}
                                >
                                    İptal
                                </button>
                                <button
                                    onClick={handleDeleteYacht}
                                    disabled={deleting}
                                    className="px-8 py-3 bg-red-500/30 backdrop-blur-sm border border-red-400/40 hover:bg-red-500/40 text-white rounded-2xl transition-all duration-200 flex items-center gap-2 disabled:opacity-50 shadow-lg shadow-black/20 font-semibold"
                                >
                                    {deleting ? (
                                        <>
                                            <div className="animate-spin rounded-full h-5 w-5 border-2 border-white border-t-transparent"></div>
                                            Siliniyor...
                                        </>
                                    ) : (
                                        <>
                                            <svg
                                                className="w-5 h-5"
                                                fill="none"
                                                stroke="currentColor"
                                                viewBox="0 0 24 24"
                                            >
                                                <path
                                                    strokeLinecap="round"
                                                    strokeLinejoin="round"
                                                    strokeWidth={2}
                                                    d="M19 7l-.867 12.142A2 2 0 0116.138 21H7.862a2 2 0 01-1.995-1.858L5 7m5 4v6m4-6v6m1-10V4a1 1 0 00-1-1h-4a1 1 0 00-1 1v3M4 7h16"
                                                />
                                            </svg>
                                            Evet, Sil
                                        </>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}
        </AdminLayout>
        </div>
    );
}

export default ViewYachtDetails;
